import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useCustomerMode } from '@plato/app-core/hooks/use-customer-mode';
import type { DefaultTimeframe } from '@plato/app-meta/default-timeframe';
import { resolveLanguage, supportedLanguages } from '@plato/app-meta/locale';
import { MetricExplainerDialog } from '@plato/app-shared/components/metric-explainer';
import { useCommercialScopesEnabled } from '@plato/feature-team-structure/hooks/use-commercial-scopes-enabled';
import { getLanguageName } from '@plato/shared-util/locale';
import { capitalize } from '@plato/shared-util/strings';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { useResolvedLanguage } from '@plato/ui-legacy/lib/hooks/locale';
import { Popover, PopoverContent, PopoverTrigger } from '@plato/ui/containers/popover';
import { PushStack, PushStackLevel } from '@plato/ui/containers/push-stack';
import { Tooltip, TooltipContent, TooltipTrigger } from '@plato/ui/primitives/tooltip';
import {
    CalculatorIcon,
    CalendarRangeIcon,
    ChartColumnIcon,
    LanguagesIcon,
    LockIcon,
    LogOutIcon,
    NetworkIcon,
    SettingsIcon,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSignOut } from '../../../lib/hooks/use-sign-out.ts';
import type { DefaultMetric } from '../../../server/utils/explorer-utils.ts';
import { api } from '../../../utils/api.ts';
import { Can } from '../../acl/can.tsx';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { ImpersonationBanner } from './impersonation-banner.tsx';
import {
    Menu,
    MenuActionRow,
    MenuBackRow,
    MenuDivider,
    MenuDrillRow,
    MenuGroup,
    MenuOptionRow,
    MenuToggleRow,
} from './menu-primitives.tsx';
import { ViewAsRow, ViewAsRows } from './view-as-menu.tsx';

/**
 * Which setting the menu has drilled into, or `undefined` for the settings level
 * itself. Both surfaces (sidebar pop-over, mobile sheet) share these names so the
 * levels below compose the same way in each. The order is the order the pushed
 * levels sit in, so each surface derives its level index from the name instead
 * of hardcoding one per sub-level.
 */
export const settingsSubLevels = ['language', 'defaultMetric', 'defaultTimeframe', 'viewAs'] as const;

export type SettingsSubLevel = (typeof settingsSubLevels)[number];

export type LanguageLevelProps = {
    onBack: () => void;
};

/** The pushed language level: back header + one row per supported language. */
export function LanguageLevel({ onBack }: LanguageLevelProps) {
    const language = useResolvedLanguage();
    const { t, i18n } = useTranslation('common');
    const sendEvent = useAnalytics();
    const utils = api.useUtils();

    const setLanguage = api.platoUser.command.settings.update.useMutation({
        onMutate: ({ preferredLanguage: next }) => {
            utils.platoUser.me.getMe.setData(undefined, (me) =>
                me && next ? { ...me, language: resolveLanguage(next) } : me,
            );
        },
        onSettled: () => utils.platoUser.me.getMe.invalidate(),
    });

    return (
        <Menu className="p-0">
            <MenuBackRow label={t('language_label', { ns: 'settings' })} onBack={onBack} />
            {supportedLanguages.map((supported) => (
                <MenuOptionRow
                    key={supported}
                    label={capitalize(getLanguageName(supported, supported) ?? supported)}
                    selected={supported === language}
                    onSelect={() => {
                        if (supported !== language) {
                            sendEvent('language_switched', {
                                from: language,
                                to: supported,
                                source: 'settings_menu',
                            });
                            setLanguage.mutate({ preferredLanguage: supported });
                        }
                        void i18n.changeLanguage(supported);
                        onBack();
                    }}
                />
            ))}
        </Menu>
    );
}

export type CommercialDefaultLevelProps = {
    onBack: () => void;
};

/** The pushed default-figure level: back header + one row per metric. */
export function DefaultMetricLevel({ onBack }: CommercialDefaultLevelProps) {
    const { t } = useTranslation('common');
    const { defaultMetric } = useAppConfig();
    const sendEvent = useAnalytics();
    const utils = api.useUtils();

    // `AppConfig` carries the effective default — this user's pick where they
    // made one, the tenant's otherwise — so the write only shows up in the
    // menu once that query has refetched.
    const { mutate } = api.platoUser.command.settings.update.useMutation({
        onSuccess: async () => {
            await utils.platoUser.query.appConfig.getAppConfig.invalidate();
        },
    });

    const options: { value: DefaultMetric; label: string }[] = [
        { value: 'Revenue', label: t('metric_revenue', { ns: 'settings' }) },
        { value: 'Order_Sum', label: t('metric_order_sum', { ns: 'settings' }) },
    ];

    return (
        <Menu className="p-0">
            <MenuBackRow
                label={t('default_metric_label', { ns: 'settings' })}
                description={t('default_metric_description', { ns: 'settings' })}
                onBack={onBack}
            />
            {options.map(({ value, label }) => (
                <MenuOptionRow
                    key={value}
                    label={label}
                    selected={value === defaultMetric}
                    onSelect={() => {
                        if (value !== defaultMetric) {
                            sendEvent('commercial_default_changed', {
                                setting: 'metric',
                                from: defaultMetric,
                                to: value,
                            });
                            mutate({ preferredMetric: value });
                        }
                        onBack();
                    }}
                />
            ))}
        </Menu>
    );
}

/** The pushed default-period level: back header + one row per timeframe. */
export function DefaultTimeframeLevel({ onBack }: CommercialDefaultLevelProps) {
    const { t } = useTranslation('common');
    const { defaultTimeframe } = useAppConfig();
    const sendEvent = useAnalytics();
    const utils = api.useUtils();

    const { mutate } = api.platoUser.command.settings.update.useMutation({
        onSuccess: () => {
            void utils.platoUser.query.appConfig.getAppConfig.invalidate();
        },
    });

    const options: { value: DefaultTimeframe; label: string }[] = [
        { value: 'YTD', label: t('timeframe_ytd', { ns: 'settings' }) },
        { value: 'TTM', label: t('timeframe_ttm', { ns: 'settings' }) },
    ];

    return (
        <Menu className="p-0">
            <MenuBackRow
                label={t('default_timeframe_label', { ns: 'settings' })}
                description={t('default_timeframe_description', { ns: 'settings' })}
                onBack={onBack}
            />
            {options.map(({ value, label }) => (
                <MenuOptionRow
                    key={value}
                    label={label}
                    selected={value === defaultTimeframe}
                    onSelect={() => {
                        if (value !== defaultTimeframe) {
                            sendEvent('commercial_default_changed', {
                                setting: 'timeframe',
                                from: defaultTimeframe,
                                to: value,
                            });
                            mutate({ preferredTimeframe: value });
                        }
                        onBack();
                    }}
                />
            ))}
        </Menu>
    );
}

export type ViewAsLevelProps = {
    onBack: () => void;
    /** Called after an actual view switch, so the surface can dismiss entirely. */
    onSwitched: () => void;
};

/** The pushed "View as" level: back header + Manager / Sales Rep. */
export function ViewAsLevel({ onBack, onSwitched }: ViewAsLevelProps) {
    const { t } = useTranslation('common');

    return (
        <>
            <Menu className="p-0">
                <MenuBackRow label={t('view_switch__view_as_label')} onBack={onBack} />
            </Menu>
            <ViewAsRows onPicked={onBack} onSwitched={onSwitched} />
        </>
    );
}

export type SettingsRowsProps = {
    onOpenLevel: (level: SettingsSubLevel) => void;
    /** Called when a row completes an action that should dismiss the surface. */
    onClose?: () => void;
};

/**
 * The settings level, grouped as the menu is drawn: "My account" holds the
 * personal preferences, customer view first because it is the one setting reps
 * change day to day, then the drill-ins as collapsed "label … value" rows and
 * the metric explainer. "Organisation" holds the tenant-level pages and renders
 * only for an administrator of a tenant the feature is live for. Account
 * actions sit below both, outside either group.
 */
export function SettingsRows({ onOpenLevel, onClose }: SettingsRowsProps) {
    const { t } = useTranslation('common');
    const navigate = useNavigate();
    const language = useResolvedLanguage();
    const { signOutFn } = useSignOut();
    const sendEvent = useAnalytics();
    const { defaultMetric, defaultTimeframe } = useAppConfig();
    const commercialScopesEnabled = useCommercialScopesEnabled();
    const [customerModeOn, setCustomerMode] = useCustomerMode();

    const metricLabel =
        defaultMetric === 'Order_Sum'
            ? t('metric_order_sum', { ns: 'settings' })
            : t('metric_revenue', { ns: 'settings' });
    const timeframeLabel =
        defaultTimeframe === 'TTM'
            ? t('timeframe_ttm', { ns: 'settings' })
            : t('timeframe_ytd', { ns: 'settings' });

    return (
        <Menu className="p-0">
            <MenuGroup label={t('group_my_account', { ns: 'settings' })}>
                <Can I="read" a="Stats" field="total_margin">
                    <MenuToggleRow
                        icon={LockIcon}
                        label={t('customer_mode__button')}
                        checked={customerModeOn}
                        onCheckedChange={setCustomerMode}
                    />
                </Can>

                <MenuDrillRow
                    icon={LanguagesIcon}
                    label={t('language_label', { ns: 'settings' })}
                    value={capitalize(getLanguageName(language, language) ?? language)}
                    onOpen={() => {
                        onOpenLevel('language');
                    }}
                />

                <MenuDrillRow
                    icon={ChartColumnIcon}
                    label={t('default_metric_label', { ns: 'settings' })}
                    value={metricLabel}
                    onOpen={() => {
                        onOpenLevel('defaultMetric');
                    }}
                />

                <MenuDrillRow
                    icon={CalendarRangeIcon}
                    label={t('default_timeframe_label', { ns: 'settings' })}
                    value={timeframeLabel}
                    onOpen={() => {
                        onOpenLevel('defaultTimeframe');
                    }}
                />

                {/* Renders nothing for non-managers. */}
                <ViewAsRow
                    onOpen={() => {
                        onOpenLevel('viewAs');
                    }}
                />

                <MenuActionRow
                    icon={CalculatorIcon}
                    label={t('metric_explainer__title', { ns: 'shared' })}
                    onSelect={() => {
                        sendEvent('metric_explainer_opened', { source: 'settings_menu' });
                        onClose?.();
                        void DialogStateManager.show(MetricExplainerDialog);
                    }}
                />
            </MenuGroup>

            {commercialScopesEnabled && (
                <>
                    <MenuDivider />
                    <MenuGroup label={t('group_organisation', { ns: 'settings' })}>
                        {/* Navigates rather than pushing a level: the editor is a page. */}
                        <MenuActionRow
                            icon={NetworkIcon}
                            label={t('label', { ns: 'team-structure' })}
                            onSelect={() => {
                                onClose?.();
                                void navigate('/team-structure');
                            }}
                        />
                    </MenuGroup>
                </>
            )}

            <MenuDivider />

            {/* Renders nothing unless a super-admin is impersonating. */}
            <li role="presentation" className="empty:hidden">
                <ImpersonationBanner expanded />
            </li>

            <MenuActionRow
                icon={LogOutIcon}
                label={t('logout__button')}
                onSelect={() => {
                    onClose?.();
                    signOutFn();
                }}
            />
        </Menu>
    );
}

/**
 * Sidebar settings entry point: everything that is not navigation or support —
 * customer view, language, the current view mode, how the figures are
 * calculated, exit demo, log out. Opens as a pop-over whose contents push
 * sideways into each setting instead of stacking a second surface on top.
 *
 * The sidebar only exists at `sm` and up. Below that the mobile bottom nav
 * owns the same levels inside its own sheet.
 */
export function SettingsMenu({ expanded = true }: { expanded?: boolean }) {
    const { t } = useTranslation('common');
    const sendEvent = useAnalytics();
    const commercialScopesEnabled = useCommercialScopesEnabled();
    const [open, setOpen] = useState(false);
    const [subLevel, setSubLevel] = useState<SettingsSubLevel | undefined>(undefined);
    const popupRef = useRef<HTMLDivElement>(null);

    // Resets subLevel itself rather than relying on onOpenChange: base-ui only
    // calls onOpenChange for its own interaction sources (outside click,
    // Escape, trigger), never when `open` is driven programmatically like
    // this is after a view switch — so that path needs its own reset too.
    const close = (): void => {
        setOpen(false);
        setSubLevel(undefined);
    };
    const backToSettings = (): void => {
        setSubLevel(undefined);
    };

    // Reset to the settings level on close so reopening never resumes mid-drill.
    const onOpenChange = (next: boolean): void => {
        setOpen(next);
        if (next) {
            sendEvent('settings_menu_opened', {
                surface: expanded ? 'sidebar' : 'rail',
                hasOrganisation: commercialScopesEnabled,
            });
        } else {
            setSubLevel(undefined);
        }
    };

    return (
        <Popover open={open} onOpenChange={onOpenChange}>
            {expanded ? (
                <PopoverTrigger
                    render={
                        <Button
                            variant="ghost"
                            className="w-full justify-start text-content"
                            leftIcon={SettingsIcon}
                        />
                    }
                >
                    <span className="font-normal">{t('button', { ns: 'settings' })}</span>
                </PopoverTrigger>
            ) : (
                <Tooltip>
                    {/* The tooltip attaches to a wrapper rather than to the button
                        itself. Rendering the tooltip trigger as the PopoverTrigger
                        makes the outer trigger's props win, which shadows the
                        popover's click handler and leaves the menu unopenable. */}
                    <TooltipTrigger render={<span className="inline-flex" />}>
                        <PopoverTrigger
                            render={
                                <Button
                                    variant="ghost"
                                    size="icon-s"
                                    aria-label={t('button', { ns: 'settings' })}
                                    icon={SettingsIcon}
                                />
                            }
                        />
                    </TooltipTrigger>
                    <TooltipContent side="right">{t('button', { ns: 'settings' })}</TooltipContent>
                </Tooltip>
            )}
            <PopoverContent
                ref={popupRef}
                side="right"
                align="end"
                className="w-[320px] p-cozy"
                initialFocus={popupRef}
            >
                {/* Level 0 is the settings level; the rest follow
                    `settingsSubLevels`, so the two orders must stay in step. */}
                <PushStack activeIndex={subLevel === undefined ? 0 : settingsSubLevels.indexOf(subLevel) + 1}>
                    <PushStackLevel>
                        <SettingsRows onOpenLevel={setSubLevel} onClose={close} />
                    </PushStackLevel>
                    <PushStackLevel>
                        <LanguageLevel onBack={backToSettings} />
                    </PushStackLevel>
                    <PushStackLevel>
                        <DefaultMetricLevel onBack={backToSettings} />
                    </PushStackLevel>
                    <PushStackLevel>
                        <DefaultTimeframeLevel onBack={backToSettings} />
                    </PushStackLevel>
                    <PushStackLevel>
                        <ViewAsLevel onBack={backToSettings} onSwitched={close} />
                    </PushStackLevel>
                </PushStack>
            </PopoverContent>
        </Popover>
    );
}
